import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { Database, Table2, RefreshCw, ChevronRight, Eye, Lock, FileCode, Columns } from 'lucide-react';

interface TableInfo {
  name: string;
  description?: string;
  row_count: number;
  has_rls: boolean;
  last_modified: string;
  columns: {
    name: string;
    type: string;
    is_nullable: boolean;
    default_value?: string;
  }[];
  usage: {
    components: string[];
    hooks: string[];
    pages: string[];
  };
}

// Definição das colunas conhecidas para cada tabela
const KNOWN_COLUMNS = {
  'acm_base': [
    { name: 'id', type: 'uuid', is_nullable: false, default_value: 'gen_random_uuid()' },
    { name: 'email', type: 'text', is_nullable: false },
    { name: 'nome', type: 'text', is_nullable: false },
    { name: 'documento', type: 'text', is_nullable: true },
    { name: 'telefone', type: 'text', is_nullable: true },
    { name: 'cursos', type: 'jsonb', is_nullable: true },
    { name: 'ultimo_acesso', type: 'timestamptz', is_nullable: true },
    { name: 'created_at', type: 'timestamptz', is_nullable: false, default_value: 'now()' }
  ],
  'jcd_alunos': [
    { name: 'id', type: 'uuid', is_nullable: false, default_value: 'gen_random_uuid()' },
    { name: 'nome', type: 'text', is_nullable: false },
    { name: 'email', type: 'text', is_nullable: false },
    { name: 'cpf', type: 'text', is_nullable: true },
    { name: 'telefone', type: 'text', is_nullable: true },
    { name: 'created_at', type: 'timestamptz', is_nullable: false, default_value: 'now()' }
  ],
  'actv_total': [
    { name: 'id', type: 'uuid', is_nullable: false, default_value: 'gen_random_uuid()' },
    { name: 'total_emails_abertos_total', type: 'integer', is_nullable: false, default_value: '0' },
    { name: 'click_through_rate_total', type: 'numeric', is_nullable: false, default_value: '0' },
    { name: 'open_rate_total', type: 'numeric', is_nullable: false, default_value: '0' },
    { name: 'sent_email_total', type: 'integer', is_nullable: false, default_value: '0' },
    { name: 'total_user_emails', type: 'integer', is_nullable: false, default_value: '0' },
    { name: 'link_vs_emails', type: 'numeric', is_nullable: false, default_value: '0' },
    { name: 'created_at', type: 'timestamptz', is_nullable: false, default_value: 'now()' }
  ],
  'acm_novo_usuario': [
    { name: 'id', type: 'uuid', is_nullable: false, default_value: 'gen_random_uuid()' },
    { name: 'nome', type: 'text', is_nullable: false },
    { name: 'email', type: 'text', is_nullable: false },
    { name: 'tipo_do_evento', type: 'text', is_nullable: false },
    { name: 'created_at', type: 'timestamptz', is_nullable: false, default_value: 'now()' }
  ],
  'actv_contato_abriu_email': [
    { name: 'id', type: 'uuid', is_nullable: false, default_value: 'gen_random_uuid()' },
    { name: 'email', type: 'text', is_nullable: false },
    { name: 'email_id', type: 'text', is_nullable: false },
    { name: 'opened_at', type: 'timestamptz', is_nullable: false },
    { name: 'created_at', type: 'timestamptz', is_nullable: false, default_value: 'now()' }
  ],
  'students': [
    { name: 'id', type: 'uuid', is_nullable: false, default_value: 'gen_random_uuid()' },
    { name: 'email', type: 'text', is_nullable: false },
    { name: 'nome', type: 'text', is_nullable: false },
    { name: 'documento', type: 'text', is_nullable: true },
    { name: 'telefone', type: 'text', is_nullable: true },
    { name: 'cursos', type: 'jsonb', is_nullable: true },
    { name: 'created_at', type: 'timestamptz', is_nullable: false, default_value: 'now()' },
    { name: 'openings_points', type: 'integer', is_nullable: false, default_value: '0' },
    { name: 'clicks_points', type: 'integer', is_nullable: false, default_value: '0' },
    { name: 'interaction_points', type: 'integer', is_nullable: false, default_value: '0' }
  ]
};

// Lista de tabelas conhecidas do sistema
const KNOWN_TABLES = Object.keys(KNOWN_COLUMNS);

// Mapeamento de RLS conhecido
const KNOWN_RLS = {
  'acm_base': true,
  'jcd_alunos': true,
  'actv_total': true,
  'acm_novo_usuario': true,
  'actv_contato_abriu_email': true,
  'students': true
};

// Mapeamento de uso das tabelas no sistema
const TABLE_USAGE = {
  'acm_base': {
    components: [
      'AcmBaseTable',
      'StudentProfileModal',
      'StudentList'
    ],
    hooks: [
      'useAcmBase',
      'useStudents'
    ],
    pages: [
      'Dashboard',
      'StudentsPage'
    ]
  },
  'jcd_alunos': {
    components: [
      'StudentProfileModal'
    ],
    hooks: [
      'useAcmBase'
    ],
    pages: [
      'StudentsPage'
    ]
  },
  'actv_total': {
    components: [
      'DetailedBarChart',
      'GroupedBarChart',
      'LineChart'
    ],
    hooks: [
      'useDashboardMetrics',
      'useEmailOpens'
    ],
    pages: [
      'Dashboard',
      'OpenRateReport',
      'EmailOpensPage'
    ]
  },
  'acm_novo_usuario': {
    components: [
      'RecentActivity'
    ],
    hooks: [
      'useRecentActivity'
    ],
    pages: [
      'Dashboard'
    ]
  },
  'actv_contato_abriu_email': {
    components: [
      'DetailedBarChart',
      'LineChart',
      'GroupedBarChart'
    ],
    hooks: [
      'useEmailOpens',
      'useDashboardMetrics'
    ],
    pages: [
      'EmailOpensPage',
      'OpenRateReport'
    ]
  },
  'students': {
    components: [
      'StudentList',
      'StudentProfileModal',
      'MetricCard'
    ],
    hooks: [
      'useStudents',
      'useMetrics',
      'useTopStudents'
    ],
    pages: [
      'Dashboard',
      'StudentsPage',
      'InteractionsReport'
    ]
  }
};

export function DatabaseTables() {
  const [tables, setTables] = useState<TableInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTable, setSelectedTable] = useState<string | null>(null);

  const getTableDescription = (tableName: string): string => {
    const descriptions: Record<string, string> = {
      'acm_base': 'Dados principais dos alunos e seus cursos',
      'jcd_alunos': 'Informações pessoais dos alunos',
      'actv_total': 'Métricas totais de atividade',
      'acm_novo_usuario': 'Registro de novos usuários e atividades',
      'actv_contato_abriu_email': 'Registro de aberturas de e-mails pelos alunos',
      'students': 'Dados completos dos alunos e métricas de engajamento'
    };

    return descriptions[tableName] || 'Tabela do sistema';
  };

  const getTableColumns = (tableName: string) => {
    return KNOWN_COLUMNS[tableName] || [];
  };

  const fetchTableInfo = async (tableName: string): Promise<TableInfo | null> => {
    try {
      const { count } = await supabase
        .from(tableName)
        .select('*', { count: 'exact', head: true });

      const columns = getTableColumns(tableName);

      return {
        name: tableName,
        description: getTableDescription(tableName),
        row_count: count || 0,
        has_rls: KNOWN_RLS[tableName] || false,
        last_modified: new Date().toISOString(),
        columns,
        usage: TABLE_USAGE[tableName] || { components: [], hooks: [], pages: [] }
      };
    } catch (err) {
      console.error(`Erro ao buscar informações da tabela ${tableName}:`, err);
      return null;
    }
  };

  const fetchTables = async () => {
    try {
      setLoading(true);
      setError(null);

      const tablesInfo = await Promise.all(
        KNOWN_TABLES.map(fetchTableInfo)
      );

      setTables(tablesInfo.filter(Boolean) as TableInfo[]);
    } catch (err) {
      console.error('Erro ao buscar tabelas:', err);
      setError('Erro ao carregar informações das tabelas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTables();
  }, []);

  if (loading) {
    return (
      <div className="space-y-4 animate-pulse">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="bg-[#2C2D3C] p-6 rounded-lg">
            <div className="h-6 bg-[#3C3D4C] rounded w-1/4"></div>
            <div className="h-4 bg-[#3C3D4C] rounded w-3/4 mt-2"></div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-500/10 text-red-400 p-4 rounded-lg">
        <p>{error}</p>
        <button
          onClick={fetchTables}
          className="mt-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
        >
          Tentar novamente
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-[#3C3D4C] rounded-lg">
            <Database className="w-5 h-5 text-emerald-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">Tabelas do Supabase</h3>
            <p className="text-sm text-gray-400">Estrutura e status das tabelas</p>
          </div>
        </div>
        <button
          onClick={fetchTables}
          className="p-2 bg-[#2C2D3C] text-gray-300 rounded-lg hover:bg-[#3C3D4C] transition-colors"
          title="Atualizar"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      <div className="space-y-3">
        {tables.map((table) => (
          <div
            key={table.name}
            className="bg-[#2C2D3C] p-4 rounded-lg hover:bg-[#3C3D4C] transition-colors cursor-pointer"
            onClick={() => setSelectedTable(selectedTable === table.name ? null : table.name)}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-[#1C1D2C] rounded-lg">
                  <Table2 className="w-4 h-4 text-emerald-400" />
                </div>
                <div>
                  <h4 className="font-medium text-white">{table.name}</h4>
                  <p className="text-sm text-gray-400">{table.description}</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  {table.has_rls && (
                    <div className="flex items-center gap-1 text-emerald-400">
                      <Lock className="w-4 h-4" />
                      <span className="text-xs">RLS</span>
                    </div>
                  )}
                  <div className="flex items-center gap-1 text-gray-400">
                    <Eye className="w-4 h-4" />
                    <span className="text-xs">{table.row_count} registros</span>
                  </div>
                  <div className="flex items-center gap-1 text-gray-400">
                    <Columns className="w-4 h-4" />
                    <span className="text-xs">
                      {table.columns.length} colunas
                    </span>
                  </div>
                  <div className="flex items-center gap-1 text-gray-400">
                    <FileCode className="w-4 h-4" />
                    <span className="text-xs">
                      {table.usage.components.length + table.usage.hooks.length + table.usage.pages.length} referências
                    </span>
                  </div>
                </div>
                <ChevronRight 
                  className={`w-4 h-4 text-gray-400 transition-transform ${
                    selectedTable === table.name ? 'rotate-90' : ''
                  }`} 
                />
              </div>
            </div>

            {selectedTable === table.name && (
              <div className="mt-4 pt-4 border-t border-gray-700">
                {/* Informações da Tabela */}
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <div>
                    <p className="text-sm text-gray-400">Total de Registros</p>
                    <p className="text-lg font-medium text-white mt-1">
                      {table.row_count.toLocaleString('pt-BR')}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-400">Row Level Security</p>
                    <p className="text-lg font-medium text-white mt-1">
                      {table.has_rls ? 'Ativado' : 'Desativado'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-400">Última Modificação</p>
                    <p className="text-lg font-medium text-white mt-1">
                      {new Date(table.last_modified).toLocaleDateString('pt-BR')}
                    </p>
                  </div>
                </div>

                {/* Colunas da Tabela */}
                <div className="mb-6">
                  <h5 className="text-sm font-medium text-gray-400 mb-3">Estrutura da Tabela</h5>
                  <div className="bg-[#1C1D2C] rounded-lg overflow-hidden">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-700">
                          <th className="px-4 py-2 text-left text-sm font-medium text-gray-400">Coluna</th>
                          <th className="px-4 py-2 text-left text-sm font-medium text-gray-400">Tipo</th>
                          <th className="px-4 py-2 text-left text-sm font-medium text-gray-400">Nullable</th>
                          <th className="px-4 py-2 text-left text-sm font-medium text-gray-400">Valor Padrão</th>
                        </tr>
                      </thead>
                      <tbody>
                        {table.columns.map((column, index) => (
                          <tr key={column.name} className="border-b border-gray-700 last:border-0">
                            <td className="px-4 py-2 text-sm font-mono text-white">{column.name}</td>
                            <td className="px-4 py-2 text-sm text-blue-400">{column.type}</td>
                            <td className="px-4 py-2 text-sm">
                              {column.is_nullable ? (
                                <span className="text-yellow-400">Sim</span>
                              ) : (
                                <span className="text-red-400">Não</span>
                              )}
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-400">
                              {column.default_value || '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Uso da tabela no sistema */}
                <div className="mt-6 space-y-4">
                  <h5 className="text-sm font-medium text-gray-400">Uso no Sistema</h5>
                  
                  {/* Componentes */}
                  {table.usage.components.length > 0 && (
                    <div>
                      <p className="text-xs text-gray-400 mb-2">Componentes</p>
                      <div className="flex flex-wrap gap-2">
                        {table.usage.components.map(component => (
                          <span key={component} className="px-2 py-1 bg-emerald-500/10 text-emerald-400 rounded text-xs">
                            {component}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Hooks */}
                  {table.usage.hooks.length > 0 && (
                    <div>
                      <p className="text-xs text-gray-400 mb-2">Hooks</p>
                      <div className="flex flex-wrap gap-2">
                        {table.usage.hooks.map(hook => (
                          <span key={hook} className="px-2 py-1 bg-purple-500/10 text-purple-400 rounded text-xs">
                            {hook}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Páginas */}
                  {table.usage.pages.length > 0 && (
                    <div>
                      <p className="text-xs text-gray-400 mb-2">Páginas</p>
                      <div className="flex flex-wrap gap-2">
                        {table.usage.pages.map(page => (
                          <span key={page} className="px-2 py-1 bg-blue-500/10 text-blue-400 rounded text-xs">
                            {page}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}